// src/modules/COGS/BreakevenTab.js
import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiDollarSign } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const BreakevenTab = () => {
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [isAddingStore, setIsAddingStore] = useState(false);

  useEffect(() => {
    // Mock data
    setStores([
      {
        id: 1,
        name: 'Store A',
        rent: 5000,
        electricity: 1000,
        water: 200,
        salaries: 15000,
        gas: 300,
        licenses: 500,
        otherExpenses: 1000,
        averageItemCost: 35
      },
      {
        id: 2,
        name: 'Store B',
        rent: 4500,
        electricity: 900,
        water: 180,
        salaries: 13000,
        gas: 250,
        licenses: 450,
        otherExpenses: 800,
        averageItemCost: 32
      }
    ]);
  }, []);

  const calculateBreakeven = (store) => {
    const totalExpenses = store.rent + store.electricity + store.water + store.salaries + store.gas + store.licenses + store.otherExpenses;
    const breakeven = totalExpenses / (1 - store.averageItemCost / 100);
    return {
      monthly: breakeven,
      daily: breakeven / 30 // Assuming 30 days in a month
    };
  };

  const StoreCard = ({ store, onEdit, onDelete }) => {
    const breakeven = calculateBreakeven(store);

    return (
      <motion.div 
        layout
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300"
      >
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-xl font-semibold">{store.name}</h3>
          <div className="flex space-x-2">
            <button onClick={() => onEdit(store)} className="text-blue-500 hover:text-blue-700">
              <FiEdit2 size={18} />
            </button>
            <button onClick={() => onDelete(store.id)} className="text-red-500 hover:text-red-700">
              <FiTrash2 size={18} />
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="col-span-2 bg-blue-100 p-3 rounded-lg">
            <h4 className="font-semibold text-blue-800">Breakeven Point</h4>
            <p className="text-blue-600">Monthly: ${breakeven.monthly.toFixed(2)}</p>
            <p className="text-blue-600">Daily: ${breakeven.daily.toFixed(2)}</p>
          </div>
          <div>
            <p className="font-medium">Rent: ${store.rent}</p>
            <p className="font-medium">Electricity: ${store.electricity}</p>
            <p className="font-medium">Water: ${store.water}</p>
            <p className="font-medium">Salaries: ${store.salaries}</p>
          </div>
          <div>
            <p className="font-medium">Gas: ${store.gas}</p>
            <p className="font-medium">Licenses: ${store.licenses}</p>
            <p className="font-medium">Other Expenses: ${store.otherExpenses}</p>
            <p className="font-medium">Avg. Item Cost: {store.averageItemCost}%</p>
          </div>
        </div>
      </motion.div>
    );
  };

  const StoreForm = ({ store, onSubmit, onCancel }) => {
    const [formData, setFormData] = useState(store || {
      name: '',
      rent: 0,
      electricity: 0,
      water: 0,
      salaries: 0,
      gas: 0,
      licenses: 0,
      otherExpenses: 0,
      averageItemCost: 0
    });

    const handleChange = (e) => {
      const value = e.target.type === 'number' ? parseFloat(e.target.value) : e.target.value;
      setFormData({ ...formData, [e.target.name]: value });
    };

    const handleSubmit = (e) => {
      e.preventDefault();
      onSubmit(formData);
    };

    return (
      <motion.form
        onSubmit={handleSubmit}
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        className="bg-white p-6 rounded-lg shadow-lg"
      >
        <h2 className="text-xl font-semibold mb-4">{store ? 'Edit Store' : 'Add New Store'}</h2>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700">Store Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
              required
            />
          </div>
          {['rent', 'electricity', 'water', 'salaries', 'gas', 'licenses', 'otherExpenses'].map((field) => (
            <div key={field}>
              <label className="block text-sm font-medium text-gray-700 capitalize">{field.replace(/([A-Z])/g, ' $1').trim()}</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiDollarSign className="text-gray-400" />
                </div>
                <input
                  type="number"
                  name={field}
                  value={formData[field]}
                  onChange={handleChange}
                  className="block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md"
                  placeholder="0.00"
                />
              </div>
            </div>
          ))}
          <div>
            <label className="block text-sm font-medium text-gray-700">Average Item Cost (%)</label>
            <input
              type="number"
              name="averageItemCost"
              value={formData.averageItemCost}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
              required
            />
          </div>
        </div>
        <div className="flex justify-end space-x-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
          >
            {store ? 'Update Store' : 'Add Store'}
          </button>
        </div>
      </motion.form>
    );
  };

  const handleAddStore = (newStore) => {
    setStores([...stores, { ...newStore, id: stores.length + 1 }]);
    setIsAddingStore(false);
  };

  const handleUpdateStore = (updatedStore) => {
    setStores(stores.map(store => store.id === updatedStore.id ? updatedStore : store));
    setSelectedStore(null);
  };

  const handleDeleteStore = (id) => {
    setStores(stores.filter(store => store.id !== id));
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Breakeven Analysis</h2>
        <button 
          onClick={() => setIsAddingStore(true)}
          className="bg-green-500 text-white px-4 py-2 rounded-full hover:bg-green-600 transition-colors duration-300 flex items-center"
        >
          <FiPlus className="mr-2" /> Add New Store
        </button>
      </div>

      <AnimatePresence>
        {(isAddingStore || selectedStore) && (
          <StoreForm 
            store={selectedStore} 
            onSubmit={selectedStore ? handleUpdateStore : handleAddStore} 
            onCancel={() => {
              setSelectedStore(null);
              setIsAddingStore(false);
            }}
          />
        )}
      </AnimatePresence>

      <motion.div layout className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <AnimatePresence>
          {stores.map(store => (
            <StoreCard 
              key={store.id} 
              store={store} 
              onEdit={setSelectedStore}
              onDelete={handleDeleteStore}
            />
          ))}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default BreakevenTab;